// ./src/utils/opResultHelpers.ts
//
// Display-time helpers that read field/generic error messages straight off an
// OpResult. There is no wrapper type: an operation's error IS its OpResult, and
// these functions only extract the strings the UI needs (Mantine input `error`
// props, alert banners). Use OpResult everywhere; reach for these only at the
// render boundary where a string is required.

import { OpResult } from "@sdflc/api-helpers";

interface OpResultErrorItem {
  name?: string;
  errors?: string[];
}

/**
 * Split an OpResult's errors into per-field messages (for the known form
 * fields) and generic messages (unnamed errors, plus any error whose field
 * isn't in the form — prefixed with its field name for context).
 */
/**
 * Does this `fields` map hold an actual refusal?
 *
 * ⚠⚠ `Object.keys(fields).length > 0` IS ALWAYS TRUE AND IS THE OBVIOUS THING TO
 * WRITE. `getFormErrors` pre-seeds EVERY known field name as an own property set to
 * `undefined` (so `fields.foo` is safe to read without a guard), so the map has one
 * key per declared field whether or not anything was refused. A page that counts keys
 * shows its "some details need fixing" banner on first paint and never hides it —
 * which is worse than showing nothing, because the banner stops meaning anything.
 * ⓘ This shipped exactly once, on the organizer-page editor (A2b #54), and it passed
 * the form audit: the audit greps for a `hasFieldErrors=` prop, which cannot tell a
 * correct expression from one that can only be true.
 *
 * ▶ Named so the right answer is reachable by autocomplete instead of retyped.
 */
export const hasAnyFieldError = (
  fields: Record<string, string | undefined> | null | undefined
): boolean => Object.values(fields ?? {}).some(Boolean);

export const getFormErrors = <T extends string>(
  result: OpResult | null | undefined,
  knownFields: readonly T[]
): { fields: Record<T, string | undefined>; genericErrors: string[] } => {
  const fields = {} as Record<T, string | undefined>;
  for (const field of knownFields) {
    fields[field] = undefined;
  }

  const genericErrors: string[] = [];
  const errors = result?.errors as OpResultErrorItem[] | undefined;

  if (!errors || !Array.isArray(errors)) {
    return { fields, genericErrors };
  }

  const knownFieldSet = new Set<string>(knownFields as readonly string[]);

  for (const item of errors) {
    const name = item?.name || "";
    const messages = item?.errors || [];
    if (messages.length === 0) continue;

    if (name === "") {
      genericErrors.push(...messages);
    } else if (knownFieldSet.has(name)) {
      fields[name as T] = messages[0];
    } else {
      genericErrors.push(...messages.map((m) => `${name}: ${m}`));
    }
  }

  return { fields, genericErrors };
};

/**
 * Build an indexed field-error name for a repeatable-rows editor:
 * `collection[index].field` (e.g. `signups[2].quantity`). Keep the notation in
 * one place so the producer (a client batch-driver, or a future server batch
 * endpoint) and `getIndexedFormErrors` (the reader) can never drift.
 */
export const indexedFieldName = (
  collection: string,
  index: number,
  field: string
): string => `${collection}[${index}].${field}`;

/**
 * Read row-scoped errors off an OpResult for a repeatable-rows editor. Error
 * names of the form `collection[index].field` (e.g. `signups[2].quantity`) are
 * grouped per row index into `byIndex[index][field]`; every other error falls to
 * `genericErrors` (unnamed as-is, named prefixed with its field for context) so
 * a banner can still show it. The row card then renders `byIndex[i][field]` right
 * on the offending input — no more guessing which of several added rows exceeded
 * a cap.
 *
 * Reusable for ANY repeatable editor whose errors are row-scoped, whether the
 * server emits the indexed names (a batch endpoint) or a client batch-driver
 * assembles them from per-row single-record calls (see SignupGuestsDrawer).
 */
export const getIndexedFormErrors = (
  result: OpResult | null | undefined,
  collection: string
): { byIndex: Record<number, Record<string, string>>; genericErrors: string[] } => {
  const byIndex: Record<number, Record<string, string>> = {};
  const genericErrors: string[] = [];
  const errors = result?.errors as OpResultErrorItem[] | undefined;

  if (!errors || !Array.isArray(errors)) {
    return { byIndex, genericErrors };
  }

  // Escape the collection name so it's safe to embed in the RegExp, then match
  // `<collection>[<index>].<field>`.
  const escaped = collection.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(`^${escaped}\\[(\\d+)\\]\\.(.+)$`);

  for (const item of errors) {
    const name = item?.name || "";
    const messages = item?.errors || [];

    if (messages.length === 0) {
      continue;
    }

    const match = name.match(pattern);

    if (match) {
      const index = Number(match[1]);
      const field = match[2];

      if (!byIndex[index]) {
        byIndex[index] = {};
      }

      if (!byIndex[index][field]) {
        byIndex[index][field] = messages[0];
      }
    } else if (name === "") {
      genericErrors.push(...messages);
    } else {
      genericErrors.push(...messages.map((m) => `${name}: ${m}`));
    }
  }

  return { byIndex, genericErrors };
};

/** First error message for a single field, or undefined. */
export const getFieldError = (
  result: OpResult | null | undefined,
  field: string
): string | undefined => {
  const fieldErrors = result?.getFieldErrors?.(field) ?? [];
  return fieldErrors.length > 0 ? fieldErrors[0] : undefined;
};

/**
 * Every message an OpResult carries, named or not, in the order they came — each trimmed, each once (ROADMAP A2h #146).
 *
 * ⚠⚠ NEVER `getErrorSummary()` FOR A MESSAGE TO SHOW. With no argument it is `getErrorSummary('')`: the UNNAMED errors
 *   only — and the server names most refusals (`plan`, `slug`, a field) — so a toast built on it was empty, or the
 *   caller's "please try again" printed over the real reason. It also keeps only the LAST of several unnamed errors
 *   (the library keys a map by name). This reads the items themselves.
 * ⓘ Takes anything: a rehydrated plain object, null, a result with no errors — a message reader must never throw.
 */
export const errorMessages = (result: { errors?: unknown } | null | undefined): string[] => {
  const items = Array.isArray(result?.errors) ? (result?.errors as OpResultErrorItem[]) : [];
  const messages: string[] = [];

  for (const item of items) {
    for (const raw of Array.isArray(item?.errors) ? item.errors : []) {
      const message = typeof raw === "string" ? raw.trim() : "";

      if (message && !messages.includes(message)) {
        messages.push(message);
      }
    }
  }

  return messages;
};

/**
 * Why the server refused, in its own words: every message, named or not, joined — `fallback` when it carries none.
 * THE reader for a toast, an alert line or an inline message (#146). A form whose fields can carry their own message
 * uses `getFormErrors` instead.
 */
export const refusalReason = (result: { errors?: unknown } | null | undefined, fallback = ""): string =>
  errorMessages(result).join(" ") || fallback;

/** First generic (unnamed) error message, or undefined. ⚠ Unnamed only — for a message to show use `refusalReason`. */
export const getGenericError = (
  result: OpResult | null | undefined
): string | undefined => {
  const genericErrors = result?.getFieldErrors?.("") ?? [];
  return genericErrors.length > 0 ? genericErrors[0] : undefined;
};

/** Join an array of messages for display; undefined when empty. */
export const formatErrors = (errors: string[]): string | undefined => {
  if (!errors || errors.length === 0) return undefined;
  return errors.join(". ");
};
